import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ProductKey } from '@/components/signup/utils/products';
import { AvailableProductsResponse } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { JourneyLocalStorage, useAnchorLocalStorage } from '@/utils/local-storage-hook';

import {
	getWacTeamTypeFromIdentity,
	USER_SEGMENTATION_TEAM_TYPES,
	UserSegmentationTeamTypes,
} from '../models/user-segmentation';
import { getJsmRecommendedTemplateId } from '../views/jsm-use-case-template-choice/jsm-use-case-template-choice-constants';

import { ConfirmationPageType } from './use-confirmation-page-type';
import { useUserSegJiraTemplateRedesign } from './use-user-seg-jira-template-redesign';

const UserSegmentationProducts = [
	ProductKey.CONFLUENCE,
	ProductKey.JIRA_SOFTWARE,
	ProductKey.JIRA_SERVICE_MANAGEMENT,
	ProductKey.SERVICE_COLLECTION,
];

const isProvisioningUserSegmentationProducts = (products: readonly string[]): boolean => {
	const otherProducts = products.filter(
		// @ts-expect-error TS 5.9 upgrade
		(product) => !UserSegmentationProducts.includes(product),
	);
	return otherProducts.length === 0;
};

export type UserSegmentationStatusContext = {
	shouldShowUserSegmentationPage: boolean;
	isProvisioningUserSegProducts: boolean;
	userSegmentationPageIsFinished: boolean;
	isUserSegHiddenByQueryParam: boolean;
	alreadyViewedUserSegmentationScreen: boolean;
	selectedTeamType: UserSegmentationTeamTypes | undefined;
	selectedTemplateId?: string;
	isTeamTypesExpanded: boolean;
	setSelectedTeamType: Dispatch<SetStateAction<UserSegmentationTeamTypes | undefined>>;
	setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
	setIsTeamTypesExpanded: Dispatch<SetStateAction<boolean>>;
	setUserSegmentationPageIsFinished: Dispatch<SetStateAction<boolean>>;
	closeUserSegmentationUI: () => void;
	userSegmentationPageType: ConfirmationPageType;
};

export const useUserSegmentationStatus = (
	products: readonly string[],
	availableProducts: AvailableProductsResponse | null,
): UserSegmentationStatusContext => {
	const { hideUserSegmentation, teamType, templateId } = useSearchParameters();
	const isPlaygroundSignup = useMemo(() => isJsmPlaygroundSignup(window.location.search), []);
	const initialTeamType =
		isPlaygroundSignup &&
		USER_SEGMENTATION_TEAM_TYPES.includes(teamType as UserSegmentationTeamTypes)
			? (teamType as UserSegmentationTeamTypes)
			: undefined;
	const initialTemplateId =
		initialTeamType && getJsmRecommendedTemplateId(initialTeamType) === templateId
			? templateId
			: undefined;
	const isUserSegHiddenByQueryParam =
		!isPlaygroundSignup && (hideUserSegmentation === 'true' || Boolean(templateId));

	/**
	 * If the user is provisioning Jira as the sole porudct, check to see if they are
	 * a n2n user or if they are n2e without any existing Jira sites.
	 */
	const { userSegmentationEnrollmentPreconditionsMet: canShowJiraUserSegmentation } =
		useUserSegJiraTemplateRedesign(availableProducts);

	const shouldDisableUserSegmentationForJira = useMemo(
		() =>
			!canShowJiraUserSegmentation &&
			products.length === 1 &&
			products.includes(ProductKey.JIRA_SOFTWARE),
		[],
	);
	const shouldDisableUserSegmentationForOtherProducts = useMemo(
		() =>
			products.length > 1 ||
			(!products.includes(ProductKey.CONFLUENCE) &&
				!products.includes(ProductKey.JIRA_SOFTWARE) &&
				!products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT) &&
				!products.includes(ProductKey.SERVICE_COLLECTION)),
		[],
	);
	const [userSegmentationPageIsFinished, setUserSegmentationPageIsFinished] = useState(
		shouldDisableUserSegmentationForJira || shouldDisableUserSegmentationForOtherProducts,
	);

	const [selectedTeamType, setSelectedTeamType] = useState<UserSegmentationTeamTypes | undefined>(
		initialTeamType,
	);
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(
		initialTemplateId,
	);
	const [isTeamTypesExpanded, setIsTeamTypesExpanded] = useState(false);
	const [alreadyViewedUserSegmentationScreen, setAlreadyViewedUserSegmentationScreen] =
		useState(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const closeUserSegmentationUI = useCallback(() => {
		const payload = { source: 'user-segmentation-ui', message: 'finished' };
		window.postMessage(JSON.stringify(payload), '*');
	}, []);

	// ATLAS-61644: Skip user seg if team type is set in local storage
	const [isTeamSetInLocalStorage, setIsTeamSetInLocalStorage] = useState(false);
	const { getAnchorLocalStorage } = useAnchorLocalStorage();

	useEffect(() => {
		if (shouldDisableUserSegmentationForJira) {
			setUserSegmentationPageIsFinished(true);
		}
	}, [shouldDisableUserSegmentationForJira]);

	useEffect(() => {
		const anchorLocalStorage = getAnchorLocalStorage();
		if (anchorLocalStorage && !isPlaygroundSignup) {
			try {
				const localStorageObj: JourneyLocalStorage = JSON.parse(anchorLocalStorage);

				if (localStorageObj.team) {
					setSelectedTeamType(getWacTeamTypeFromIdentity(localStorageObj.team));
					setUserSegmentationPageIsFinished(true);
					setIsTeamSetInLocalStorage(true);
				}
			} catch (err) {
				console.log(err);
			}
		}
	}, []);

	const listenForUserSegmentationPage = (event: MessageEvent) => {
		let data;
		try {
			data = JSON.parse(event.data);
			if (data.source !== 'user-segmentation-ui') {
				data = null;
			}
		} catch {
			data = null;
		}
		if (!data) {
			return;
		}
		if (!data.message) {
			return;
		}
		if (data.message === 'finished') {
			if (data.error !== 'shouldView was false') {
				createAnalyticsEvent({
					eventName: 'userSegmentationPageClosed',
					eventType: 'UI',
					eventComponent: 'userSegmentationPage',
					event: 'closed',
					products,
				}).fire('signup');
			} else {
				/* The iFramed user segmentation screen makes an API call to check if the user has viewed
        the screen before. If they have, the screen will NOT show again. */
				setAlreadyViewedUserSegmentationScreen(true);
			}
			setUserSegmentationPageIsFinished(true);
		}

		if (data.message === 'rendered') {
			createAnalyticsEvent({
				eventName: 'userSegmentationPageViewed',
				eventComponent: 'userSegmentationPage',
				event: 'viewed',
			}).fire('signup');
		}
	};

	useEffect(() => {
		window.addEventListener('message', listenForUserSegmentationPage, false);

		return () => {
			window.removeEventListener('message', listenForUserSegmentationPage, false);
		};
	}, [userSegmentationPageIsFinished]);

	const userSegmentationPageType = getUserSegmentationPageType(products);

	return {
		/* isEligibleToSeeUserSegmentationPage is agnostic to whether the user has already seen the user segmentation screen.
    since the iFramed screen itself handles this. */
		shouldShowUserSegmentationPage:
			isProvisioningUserSegmentationProducts(products) &&
			!userSegmentationPageIsFinished &&
			!isUserSegHiddenByQueryParam &&
			(!isTeamSetInLocalStorage || isPlaygroundSignup) &&
			!shouldDisableUserSegmentationForJira &&
			!shouldDisableUserSegmentationForOtherProducts,
		isProvisioningUserSegProducts: isProvisioningUserSegmentationProducts(products),
		userSegmentationPageIsFinished,
		isUserSegHiddenByQueryParam,
		alreadyViewedUserSegmentationScreen,
		setUserSegmentationPageIsFinished,
		closeUserSegmentationUI,
		selectedTeamType,
		selectedTemplateId,
		isTeamTypesExpanded,
		setSelectedTeamType,
		setSelectedTemplateId,
		setIsTeamTypesExpanded,
		userSegmentationPageType,
	};
};

function getUserSegmentationPageType(products: readonly string[]): ConfirmationPageType {
	if (products.length === 1 && products.includes(ProductKey.JIRA_SOFTWARE)) {
		return 'jira-user-segmentation';
	}

	return 'user-segmentation';
}
